import { useEffect, useRef, useState } from 'react'
import { FaChevronDown } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { Button, Menu, MenuItem, MenuLabel } from '../../../ui'
import { ExportGroupBy } from '../commsShared/exportDownload'
import styles from './styles.module.scss'

const fmt = (n: number) => n.toLocaleString('en-GB')

export interface StatLine {
    value: number
    label: string
    /** Greyed out when zero (default true): a "0 this week" line reads as quiet, not as a warning. */
    quietWhenZero?: boolean
}

/** Who a group of emails (or threads) came from: a client, a lender, or neither (unknown, internal, court, FOS). */
export interface SenderSplit {
    client: number
    lender: number
    other: number
}

/** True when more than one kind of sender is present, so a split is worth a card of its own. */
export const isMixed = (from: SenderSplit | null | undefined): from is SenderSplit => Boolean(from) && [from!.client, from!.lender, from!.other].filter((n) => n > 0).length > 1

export const sumSenders = (parts: (SenderSplit | null | undefined)[]): SenderSplit =>
    parts.reduce<SenderSplit>((acc, p) => (p ? { client: acc.client + p.client, lender: acc.lender + p.lender, other: acc.other + p.other } : acc), { client: 0, lender: 0, other: 0 })

/**
 * "From 1,116 clients · 238 lenders · 212 other" on one line. Parts that are
 * zero are left out, so a lender-only mailbox reads "From 3,051 lenders".
 */
export const SenderLine = ({ from, className }: { from: SenderSplit; className?: string }) => {
    const parts = [
        from.client > 0 ? { n: from.client, label: from.client === 1 ? 'client' : 'clients' } : null,
        from.lender > 0 ? { n: from.lender, label: from.lender === 1 ? 'lender' : 'lenders' } : null,
        from.other > 0 ? { n: from.other, label: 'neither' } : null,
    ].filter((p): p is { n: number; label: string } => p !== null)
    if (!parts.length) return null
    return (
        <span className={className} title="Who these came from: a client, a lender, or neither (unknown sender, internal, court, FOS)">
            From{' '}
            {parts.map((p, i) => (
                <span key={p.label}>
                    {i > 0 && ' · '}
                    <strong>{fmt(p.n)}</strong> {p.label}
                </span>
            ))}
        </span>
    )
}

/**
 * The figures of one category card, one per line with the number in bold, so
 * a card reads at a glance instead of as a sentence of small text. `from`
 * adds the sender split as its own line.
 */
export const CategoryStats = ({ lines, from = null, className = '' }: { lines: StatLine[]; from?: SenderSplit | null; className?: string }) => (
    <ul className={`${styles.categoryStats} ${className}`}>
        {lines.map((l) => (
            <li key={l.label} className={l.value === 0 && l.quietWhenZero !== false ? styles.categoryStatQuiet : undefined}>
                <strong>{fmt(l.value)}</strong> {l.label}
            </li>
        ))}
        {from && (from.client || from.lender || from.other) > 0 && (
            <li>
                <SenderLine from={from} />
            </li>
        )}
    </ul>
)

/** What each report in the menu is, in the order offered. */
export const REPORT_OPTIONS: { groupBy: ExportGroupBy; label: string; hint: string }[] = [
    { groupBy: 'lender', label: 'By lender', hint: 'One line per lender: how many, how many with a claim, awaiting us, newest and oldest' },
    { groupBy: 'claim', label: 'By claim reference', hint: 'One line per claim with its client, lender, latest email and a link' },
    { groupBy: 'client', label: 'By client', hint: 'One line per client with their claims, lenders and latest email' },
]

interface ReportMenuProps {
    /** "email" or "thread": what a plain download lists one row of. */
    unit: 'email' | 'thread'
    /** null = every row, no grouped sheet. */
    onExport: (groupBy: ExportGroupBy | null) => void
    exporting?: boolean
    /** Small link-style trigger (inside a compact card) or a ghost button. */
    variant?: 'link' | 'ghost'
    label?: string
}

/**
 * "Report ▾" on a category card: download every row, or an analytics report
 * grouped by lender, claim reference or client (.xlsx, grouped sheet first,
 * then every row with its client and claim).
 */
export const ReportMenu = ({ unit, onExport, exporting = false, variant = 'link', label = 'Report' }: ReportMenuProps) => {
    const [open, setOpen] = useState(false)
    const wrapRef = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        if (!open) return
        const close = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
        }
        const esc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        document.addEventListener('mousedown', close)
        document.addEventListener('keydown', esc)
        return () => {
            document.removeEventListener('mousedown', close)
            document.removeEventListener('keydown', esc)
        }
    }, [open])

    const pick = (groupBy: ExportGroupBy | null) => {
        setOpen(false)
        onExport(groupBy)
    }

    return (
        <span className={styles.reportWrap} ref={wrapRef} onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant={variant} iconRight={<FaChevronDown aria-hidden />} loading={exporting} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)} title="Download a report of these as .xlsx">
                {label}
            </Button>
            {open && (
                <Menu className={styles.reportMenu} aria-label="Report">
                    <MenuLabel>Download .xlsx</MenuLabel>
                    <MenuItem onClick={() => pick(null)} title={`One row per ${unit}, each with its client and claim where we have one`}>
                        Every {unit}
                    </MenuItem>
                    <MenuLabel>Analytics</MenuLabel>
                    {REPORT_OPTIONS.map((o) => (
                        <MenuItem key={o.groupBy} onClick={() => pick(o.groupBy)} title={o.hint}>
                            {o.label}
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </span>
    )
}

/** "See actions →": through to the Agent Actions page for this category at this level. */
export const SeeActionsLink = ({ href, variant = 'link' }: { href: string; variant?: 'link' | 'ghost' }) => {
    const navigate = useNavigate()
    return (
        <Button
            size="sm"
            variant={variant}
            onClick={(e) => {
                e.stopPropagation()
                navigate(href)
            }}
            title="What happens next for these: the agent actions for this category"
        >
            See actions →
        </Button>
    )
}

/** The two links under a category card's figures. */
export const CategoryActions = ({ children }: { children: React.ReactNode }) => <span className={styles.categoryActions}>{children}</span>
